import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Building2, Users, Phone, GitBranch, Brain,
  Clock, PlayCircle, Settings, LogOut, Menu, X, ChevronLeft, ChevronRight,
  BarChart3,
} from 'lucide-react';
import { vdAuthApi } from './api';

const navItems = [
  { to: '/voicedesk/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/voicedesk/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/voicedesk/company', label: 'Company Setup', icon: Building2 },
  { to: '/voicedesk/departments', label: 'Departments', icon: Phone },
  { to: '/voicedesk/directory', label: 'Directory', icon: Users },
  { to: '/voicedesk/routing', label: 'Call Routing', icon: GitBranch },
  { to: '/voicedesk/ai', label: 'AI Service', icon: Brain },
  { to: '/voicedesk/hours', label: 'Business Hours', icon: Clock },
  { to: '/voicedesk/test', label: 'Test IVR', icon: PlayCircle },
  { to: '/voicedesk/settings', label: 'Settings', icon: Settings },
];

export default function VDLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authed, setAuthed] = useState(null); // null=checking, true/false

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    // Check for unified auth token first, then legacy vd_token
    const token = localStorage.getItem('auth_token') || localStorage.getItem('vd_token');
    if (!token) { navigate('/login', { replace: true }); return; }
    // Ensure vd_token is set for VoiceDesk API calls
    if (!localStorage.getItem('vd_token') && localStorage.getItem('auth_token')) {
      localStorage.setItem('vd_token', localStorage.getItem('auth_token'));
    }
    vdAuthApi.me().then(() => setAuthed(true)).catch(() => {
      // Try unified auth endpoint as fallback
      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/unified-auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }).then(r => r.ok ? setAuthed(true) : Promise.reject())
          .catch(() => {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('vd_token');
            navigate('/login', { replace: true });
          });
      } else {
        localStorage.removeItem('vd_token');
        navigate('/login', { replace: true });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authed === null) return <div className="h-screen bg-[#0f1117] flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>;

  const handleLogout = () => {
    localStorage.removeItem('vd_token');
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-16 border-b border-white/10 shrink-0">
        <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0"><img src="/assets/voicedesk-logo.png" alt="VoiceDesk" className="w-full h-full object-cover" /></div>
        {!collapsed && <span className="font-bold text-lg text-foreground tracking-tight">VoiceDesk</span>}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'text-muted-foreground hover:text-foreground hover:bg-card/5'
              }`
            }
          >
            <Icon className="w-[18px] h-[18px] shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="border-t border-white/10 p-3 shrink-0">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-red-400 hover:bg-card/5 w-full transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#0f1117] text-foreground overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col border-r border-white/10 bg-[#141721] transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}>
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-4 left-2 p-1 text-muted-foreground hover:text-foreground"
          style={{ left: collapsed ? '16px' : '208px' }}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 h-full bg-[#141721] border-r border-white/10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-white/10 flex items-center px-4 gap-3 shrink-0 bg-[#141721]/50">
          <button className="lg:hidden text-muted-foreground hover:text-foreground" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">VoiceDesk IVR</span>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
